import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useToast } from '../context/ToastContext';

interface ConnectorRow { id: string; kind: string; name: string; auto_push: boolean; active: boolean; config: Record<string, unknown>; }
interface OutboxRow { id: number; channel: string; target: string; status: string; attempts: number; last_error: string | null; next_at: string; created_at: string; }

const KIND_LABELS: Record<string, string> = { comptapro: 'ComptaPro.ma', odoo: 'Odoo', rest: 'REST générique' };

export function Connectors() {
  const toast = useToast();
  const [list, setList] = useState<ConnectorRow[] | null>(null);
  const [outbox, setOutbox] = useState<OutboxRow[] | null>(null);
  const [outboxStatus, setOutboxStatus] = useState<'dead' | 'pending' | 'sent'>('dead');
  const [busy, setBusy] = useState(false);

  const [kind, setKind] = useState('comptapro');
  const [name, setName] = useState('ComptaPro');
  const [apiKeyEnv, setApiKeyEnv] = useState('COMPTAPRO_API_KEY');
  const [baseUrl, setBaseUrl] = useState('');
  const [statut, setStatut] = useState<'Envoyée' | 'Brouillon'>('Envoyée');
  const [autoPush, setAutoPush] = useState(true);
  const [rawConfig, setRawConfig] = useState('{}');

  const [pushConnectorId, setPushConnectorId] = useState('');
  const [pushNumber, setPushNumber] = useState('');

  async function load() {
    try {
      const [c, o] = await Promise.all([
        api<ConnectorRow[]>('/v1/connectors'),
        api<OutboxRow[]>(`/v1/outbox?status=${outboxStatus}`),
      ]);
      setList(c); setOutbox(o);
    } catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur de chargement', true); }
  }

  useEffect(() => { load(); }, [outboxStatus]);

  async function createConnector() {
    let config: Record<string, unknown>;
    if (kind === 'comptapro') {
      config = { api_key_env: apiKeyEnv, statut };
      if (baseUrl.trim()) config.base_url = baseUrl.trim();
    } else {
      try { config = JSON.parse(rawConfig); }
      catch { toast('Configuration JSON invalide', true); return; }
    }
    setBusy(true);
    try {
      await api('/v1/connectors', { method: 'POST', body: { kind, name, config, auto_push: autoPush } });
      toast('Connecteur créé');
      setName(''); setRawConfig('{}');
      load();
    } catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
    finally { setBusy(false); }
  }

  async function removeConnector(id: string) {
    if (!confirm('Désactiver ce connecteur ?')) return;
    try { await api(`/v1/connectors/${id}`, { method: 'DELETE' }); toast('Connecteur désactivé'); load(); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
  }

  async function pushInvoice() {
    if (!pushConnectorId || !pushNumber.trim()) { toast('Choisissez un connecteur et un numéro de facture', true); return; }
    try {
      const d = await api<{ queued: number }>(`/v1/connectors/${pushConnectorId}/push`, { method: 'POST', body: { number: pushNumber.trim() } });
      toast(d.queued ? "Facture mise en file d'envoi" : 'Rien à envoyer (connecteur inactif ?)');
      setPushNumber('');
      load();
    } catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
  }

  async function retry(id: number) {
    try { await api(`/v1/outbox/${id}/retry`, { method: 'POST' }); toast('Remis en file'); load(); }
    catch (e) { toast(e instanceof ApiError ? e.message : 'Erreur', true); }
  }

  return (
    <>
      <div className="pagehead"><h1>ComptaPro</h1></div>

      <div className="card">
        <h2>Connecteurs configurés</h2>
        {list && list.length === 0 && <div className="muted">Aucun connecteur configuré.</div>}
        {list && list.length > 0 && (
          <div className="tblwrap"><table><tbody>
            <tr><th>Nom</th><th>Type</th><th>Auto-envoi</th><th>Statut</th><th></th></tr>
            {list.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td>{KIND_LABELS[c.kind] || c.kind}</td>
                <td>{c.auto_push ? 'Oui' : 'Non'}</td>
                <td><span className={'badge ' + (c.active ? 'ok' : 'crit')}>{c.active ? 'actif' : 'désactivé'}</span></td>
                <td>{c.active && <button onClick={() => removeConnector(c.id)}>Désactiver</button>}</td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </div>

      <div className="card">
        <h2>Ajouter un connecteur</h2>
        <label htmlFor="ck">Type</label>
        <select id="ck" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="comptapro">ComptaPro.ma</option>
          <option value="odoo">Odoo</option>
          <option value="rest">REST générique</option>
        </select>

        <label htmlFor="cn">Nom</label>
        <input id="cn" value={name} onChange={(e) => setName(e.target.value)} placeholder="ComptaPro" />

        {kind === 'comptapro' ? (
          <>
            <label htmlFor="cke">Variable d'environnement contenant la clé API</label>
            <input id="cke" value={apiKeyEnv} onChange={(e) => setApiKeyEnv(e.target.value)} placeholder="COMPTAPRO_API_KEY" />
            <div className="muted" style={{ margin: '0.3rem 0 0.8rem' }}>
              La clé elle-même n'est jamais stockée ici : elle doit être définie comme variable d'environnement
              du service worker sur le serveur.
            </div>
            <label htmlFor="cbu">Adresse de l'API (optionnel)</label>
            <input id="cbu" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://comptapro.ma/api/v1" />
            <label htmlFor="cst">Statut des factures envoyées</label>
            <select id="cst" value={statut} onChange={(e) => setStatut(e.target.value as 'Envoyée' | 'Brouillon')}>
              <option value="Envoyée">Envoyée (finale)</option>
              <option value="Brouillon">Brouillon (à valider dans ComptaPro)</option>
            </select>
          </>
        ) : (
          <>
            <label htmlFor="craw">Configuration (JSON)</label>
            <textarea id="craw" value={rawConfig} onChange={(e) => setRawConfig(e.target.value)}
              placeholder={kind === 'odoo' ? '{"url":"https://...","db":"...","login":"...","password_env":"ODOO_PW"}'
                : '{"url":"https://...","template":{},"headers":{}}'} />
          </>
        )}

        <label className="row" style={{ alignItems: 'center', gap: '6px', margin: '0.6rem 0' }}>
          <input type="checkbox" checked={autoPush} onChange={(e) => setAutoPush(e.target.checked)} />
          Envoyer automatiquement chaque facture émise
        </label>

        <button className="primary" onClick={createConnector} disabled={busy || !name.trim()}>Créer le connecteur</button>
      </div>

      <div className="card">
        <h2>Renvoyer une facture</h2>
        <label htmlFor="pc">Connecteur</label>
        <select id="pc" value={pushConnectorId} onChange={(e) => setPushConnectorId(e.target.value)}>
          <option value="">— choisir —</option>
          {list?.filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <label htmlFor="pn">Numéro de facture</label>
        <input id="pn" value={pushNumber} onChange={(e) => setPushNumber(e.target.value)} placeholder="ex: 2026-0042" />
        <div className="row" style={{ marginTop: '0.6rem' }}>
          <button className="primary" onClick={pushInvoice}>Envoyer</button>
        </div>
      </div>

      <div className="card">
        <h2>File d'envoi</h2>
        <label htmlFor="os">Statut</label>
        <select id="os" value={outboxStatus} onChange={(e) => setOutboxStatus(e.target.value as 'dead' | 'pending' | 'sent')}>
          <option value="dead">Échecs (abandonnés)</option>
          <option value="pending">En attente</option>
          <option value="sent">Envoyés</option>
        </select>
        {outbox && outbox.length === 0 && <div className="muted" style={{ marginTop: '0.6rem' }}>Rien à afficher.</div>}
        {outbox && outbox.length > 0 && (
          <div className="tblwrap"><table><tbody>
            <tr><th>Canal</th><th>Cible</th><th>Tentatives</th><th>Dernière erreur</th><th>Prochain essai</th><th></th></tr>
            {outbox.map((o) => (
              <tr key={o.id}>
                <td>{o.channel}</td>
                <td>{o.target}</td>
                <td>{o.attempts}</td>
                <td>{o.last_error || '—'}</td>
                <td>{o.next_at}</td>
                <td>{o.status === 'dead' && <button onClick={() => retry(o.id)}>Relancer</button>}</td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </div>
    </>
  );
}
